import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { more } from '@/assets/icons';
import {
	ActionSheet,
	ActionSheetAction,
} from '@/components/shared/ActionSheet';
import { AsaphAvatar } from '@/components/shared/Avatar';
import { AsaphIconButton } from '@/components/shared/Button';
import { Chip } from '@/components/shared/Chip';
import { NotificationBadge } from '@/components/shared/NotificationBadge';
import {
	getOnlineStatusToString,
	OnlineStatusFormatter,
} from '@/helpers/timeFormatter';
import { ListItem, ListItemText, SvgIcon } from '@material-ui/core';
import { S3Object } from 'models';

import { UserAvatar } from '../UserAvatar';
import { InvitedMemberAvatar } from './components/InvitedMemberAvatar';
import styles from './TeamMemberCard.module.scss';

export interface TeamMemberProps {
	id: string;
	name: string;
	email: string;
	role?: string | null;
	invitePending: boolean;
	isSelf: boolean;
	isAdmin: boolean;
	lastActive: string | null;
	actions: ActionSheetAction[];
	profilePhoto?: S3Object;
	onRowPress?: () => void;
}

const lastActiveUpdateInterval = 10 * 1000; // 10 seconds
export const TeamMemberCard: React.FC<TeamMemberProps> = ({
	id,
	name,
	email,
	role,
	isSelf,
	invitePending,
	isAdmin,
	actions,
	lastActive,
	profilePhoto,
	onRowPress,
}) => {
	const getSubtitleText = () => {
		if (isSelf) {
			return 'You';
		}

		if (invitePending) {
			return `Invited by email`;
		}

		return role || email;
	};

	const [open, setOpen] = useState(false);
	const handleDialogOpen = useCallback(() => setOpen(true), [setOpen]);
	const handleDialogClose = useCallback(() => setOpen(false), [setOpen]);

	let avatar;
	if (invitePending) {
		avatar = <InvitedMemberAvatar name={name} />;
	} else {
		avatar = <UserAvatar size="small" userId={id} />;
	}

	const nameClasses = [
		styles['member__name'],
		invitePending ? styles['member__name--invited'] : '',
	];

	const statusClasses = [
		styles['member__status'],
		invitePending ? styles['member__status--invited'] : '',
	];

	return (
		<>
			<ListItem key={`m-${id}`} disableGutters className={styles['member__row']} onClick={onRowPress} style={onRowPress ? { cursor: 'pointer' } : undefined}>
				<div className={styles['member__avatar-container']}>
					{avatar}
				</div>
				<ListItemText
					primary={name}
					secondary={getSubtitleText()}
					classes={{
						primary: nameClasses.join(' '),
						secondary: statusClasses.join(' '),
					}}
				/>
				{!invitePending && isAdmin && (
					<div className={styles['member__trailing-chip']}>
						<Chip variant="rounded" color="light-gray">Admin</Chip>
					</div>
				)}
				{actions.length > 0 && (
					<AsaphIconButton
						icon={<SvgIcon fontSize="inherit" component={more} />}
						size="small"
						color="grey"
						aria-label="Show Additional Actions"
						transparent
						onClick={handleDialogOpen}
					/>
				)}
			</ListItem>
			<ActionSheet open={open} title={name} onClose={handleDialogClose}>
				{actions.map((action) => {
					return (
						<ActionSheetAction
							key={action.label}
							label={action.label}
							onActionClick={action.onActionClick}
							clickContext={{ handleDialogClose }}
							type={action.type}
						/>
					);
				})}
			</ActionSheet>
		</>
	);
};

function getOnlineStatusFormatter(isInvite: boolean): OnlineStatusFormatter {
	let prefix = isInvite ? 'Invited' : 'Active';

	return {
		toSeconds: (value: number) => `${prefix} now`,
		toMinutes: (value: number) =>
			`${prefix} ${value} minute${value > 1 ? 's' : ''} ago`,
		toHours: (value: number) =>
			`${prefix} ${value} hour${value > 1 ? 's' : ''} ago`,
		toDays: (value: number) =>
			`${prefix} ${value} day${value > 1 ? 's' : ''} ago`,
	};
}
